import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { brl } from "../lib/format";
import { MagnifyingGlass, Crown, TrendUp, Users, ChartLineUp, ArrowRight, X } from "@phosphor-icons/react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const CHART_TOOLTIP_STYLE = { background: "#FFF", border: "2px solid #0A0A0A", boxShadow: "4px 4px 0 #0A0A0A", borderRadius: 0 };
const CHART_TICK_STYLE = { fill: "#0A0A0A", fontSize: 11, fontWeight: 700 };
const CHART_TICK_SMALL = { fill: "#0A0A0A", fontSize: 11 };
const CHART_MARGIN = { top: 5, right: 5, left: 5, bottom: 5 };
const CHART_LINE_DOT = { r: 5, fill: "#FFD400", stroke: "#0A0A0A", strokeWidth: 2 };
const BRL_TICK = (v) => `R$${v}`;

const MONTHS_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const fmtMonth = (ym) => {
  if (!ym || ym.length < 7) return ym || "";
  const [y, m] = ym.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MONTHS_PT[idx] || m}/${y.slice(2)}`;
};

export default function Loyalty() {
  const [data, setData] = useState({ handles: [], totals: {}, monthly_totals: [], available_months: [], segments: {} });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [segmentFilter, setSegmentFilter] = useState("all"); // all | vip | recorrente | novo
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const { data } = await api.get("/loyalty", { params });
    setData(data);
    setLoading(false);
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = data.handles;
    if (segmentFilter !== "all") {
      list = list.filter(h => h.segment === segmentFilter);
    }
    if (!term) return list;
    return list.filter(h =>
      h.handle.toLowerCase().includes(term) ||
      (h.customer?.full_name || "").toLowerCase().includes(term),
    );
  }, [q, data.handles, segmentFilter]);

  const top10 = useMemo(() => data.handles.slice(0, 10).map(h => ({
    handle: h.handle.length > 14 ? h.handle.slice(0, 12) + "…" : h.handle,
    total: h.total_spent,
  })), [data.handles]);

  const monthlyChart = useMemo(() => (data.monthly_totals || []).map(m => ({
    month: fmtMonth(m.month),
    raw: m.month,
    total: m.total,
  })), [data.monthly_totals]);

  const t = data.totals || {};
  const availableMonths = data.available_months || [];
  const hasPeriod = !!(start || end);

  return (
    <div data-testid="loyalty-page" className="space-y-8">
      <header>
        <p className="label-brutal">Programa de fidelidade</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black">Fidelidade</h1>
        <p className="text-brand-ink2 mt-2">Ranking de clientes, frequência de compra e histórico por @arroba.</p>
      </header>

      <div data-testid="loy-period-filter" className="card-brutal p-4 bg-brand-cream grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
        <div>
          <label className="label-brutal">Início (mês/ano)</label>
          <select data-testid="loy-start" className="input-brutal" value={start} onChange={(e) => setStart(e.target.value)}>
            <option value="">— sem limite —</option>
            {availableMonths.map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
          </select>
        </div>
        <div>
          <label className="label-brutal">Fim (mês/ano)</label>
          <select data-testid="loy-end" className="input-brutal" value={end} onChange={(e) => setEnd(e.target.value)}>
            <option value="">— sem limite —</option>
            {availableMonths.map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
          </select>
        </div>
        {hasPeriod && (
          <button data-testid="loy-clear-period" onClick={() => { setStart(""); setEnd(""); }} className="btn-white h-[52px]">
            <X weight="bold" size={16} /> Limpar
          </button>
        )}
        <div className="text-xs text-brand-ink2 md:text-right">
          {hasPeriod
            ? <span className="badge-brutal !bg-brand-yellow">Filtrando: {start ? fmtMonth(start) : "início"} → {end ? fmtMonth(end) : "hoje"}</span>
            : <span>Sem filtro · exibindo todo o histórico</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="loy-stat-revenue" label="Faturamento total" value={brl(t.total_revenue || 0)} bg="bg-brand-mint" icon={TrendUp} />
        <Stat testid="loy-stat-paid" label="Recebido" value={brl(t.total_paid || 0)} bg="bg-brand-yellow" icon={ChartLineUp} />
        <Stat testid="loy-stat-clients" label="Clientes únicos" value={t.unique_handles || 0} bg="bg-brand-purple" icon={Users} />
        <Stat
          testid="loy-stat-top"
          label="Top comprador"
          value={t.top_spender?.handle || "—"}
          hint={t.top_spender ? brl(t.top_spender.total) : ""}
          bg="bg-brand-pink text-white"
          icon={Crown}
        />
      </div>

      <div className="card-brutal p-4 bg-white flex flex-wrap items-center gap-2">
        <span className="label-brutal !mb-0">Segmentos:</span>
        <SegChip active={segmentFilter === "all"} onClick={() => setSegmentFilter("all")} testid="seg-all">
          Todos · {data.handles.length}
        </SegChip>
        <SegChip active={segmentFilter === "vip"} onClick={() => setSegmentFilter("vip")} testid="seg-vip" bg="bg-brand-pink text-white">
          VIP · {data.segments?.vip || 0}
        </SegChip>
        <SegChip active={segmentFilter === "recorrente"} onClick={() => setSegmentFilter("recorrente")} testid="seg-recorrente" bg="bg-brand-mint">
          Recorrente · {data.segments?.recorrente || 0}
        </SegChip>
        <SegChip active={segmentFilter === "novo"} onClick={() => setSegmentFilter("novo")} testid="seg-novo" bg="bg-brand-yellow">
          Novo · {data.segments?.novo || 0}
        </SegChip>
        <span className="text-xs text-brand-ink2 ml-2">
          VIP: top 20% em gasto e ≥ 2 pedidos. Recorrente: ≥ 2 pedidos. Novo: 1 pedido.
        </span>
      </div>

      {monthlyChart.length > 0 && (
        <div data-testid="loy-evolution-chart" className="card-brutal p-6">
          <h2 className="font-heading text-xl font-black mb-4">Evolução mensal · faturamento</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyChart} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0A0A0A22" />
                <XAxis dataKey="month" tick={CHART_TICK_STYLE} interval={0} />
                <YAxis tick={CHART_TICK_SMALL} tickFormatter={BRL_TICK} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => brl(v)} />
                <Line type="monotone" dataKey="total" stroke="#AB0000" strokeWidth={3} dot={CHART_LINE_DOT} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {top10.length > 0 && (
        <div className="card-brutal p-6">
          <h2 className="font-heading text-xl font-black mb-4">Top 10 · gasto acumulado</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={top10} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0A0A0A22" />
                <XAxis dataKey="handle" tick={CHART_TICK_STYLE} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis tick={CHART_TICK_SMALL} tickFormatter={BRL_TICK} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => brl(v)} />
                <Bar dataKey="total" fill="#AB0000" stroke="#1A1A1A" strokeWidth={2} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="card-brutal p-4 flex items-center gap-3 bg-brand-yellow">
        <MagnifyingGlass weight="bold" size={18} />
        <input
          data-testid="loy-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por arroba ou nome…"
          className="bg-transparent focus:outline-none flex-1 font-bold"
        />
      </div>

      <div className="card-brutal overflow-x-auto">
        <table className="tbl w-full">
          <thead>
            <tr>
              <th className="w-8">#</th>
              <th>Arroba</th>
              <th>Nome</th>
              <th className="w-24">Segmento</th>
              <th className="w-56">Próximo nível</th>
              <th className="text-right">Pedidos</th>
              <th className="text-right">Lives</th>
              <th className="text-right">Ticket médio</th>
              <th className="text-right">Total gasto</th>
              <th className="text-right">Pendente</th>
              <th className="text-right">Última compra</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={12} className="text-center text-brand-ink2 py-8">Carregando…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={12} className="text-center text-brand-ink2 py-8">Nenhum cliente encontrado.</td></tr>
            )}
            {filtered.map((h) => (
              <tr
                key={h.handle}
                data-testid={`loy-row-${h.handle}`}
                onClick={() => nav(`/loyalty/${encodeURIComponent(h.handle)}`)}
                className="cursor-pointer"
              >
                <td className="font-bold">{data.handles.indexOf(h) + 1}</td>
                <td className="font-bold whitespace-nowrap">{h.handle}</td>
                <td>{h.customer?.full_name || <span className="text-brand-ink2">sem cadastro</span>}</td>
                <td data-testid={`seg-badge-${h.handle}`}><SegmentBadge segment={h.segment} /></td>
                <td data-testid={`next-tier-${h.handle}`}><TierProgress h={h} /></td>
                <td className="text-right">{h.orders_count}</td>
                <td className="text-right">{h.lives_count}</td>
                <td className="text-right">{brl(h.avg_ticket)}</td>
                <td className="text-right font-bold">{brl(h.total_spent)}</td>
                <td className="text-right">
                  {h.total_pending > 0 ? <span className="badge-brutal bg-brand-peach">{brl(h.total_pending)}</span> : <span className="badge-brutal bg-brand-success text-white">quitado</span>}
                </td>
                <td className="text-right text-brand-ink2 text-sm">
                  {h.days_since_last !== null ? `${h.days_since_last}d` : "—"}
                </td>
                <td className="text-right"><ArrowRight weight="bold" size={14} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, hint, bg, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className={`card-brutal p-5 ${bg}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-bold uppercase tracking-widest">{label}</p>
        <Icon weight="bold" size={18} />
      </div>
      <p className="font-heading text-2xl sm:text-3xl font-black break-all">{value}</p>
      {hint && <p className="text-xs text-brand-ink2 mt-1">{hint}</p>}
    </div>
  );
}

function SegChip({ active, onClick, children, testid, bg = "bg-white" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 border-2 border-brand-ink text-xs font-bold uppercase tracking-wide transition-all ${
        active ? `${bg} shadow-brutalSm` : "bg-white hover:bg-brand-cream"
      }`}
    >
      {children}
    </button>
  );
}

function SegmentBadge({ segment }) {
  if (segment === "vip") return <span className="badge-brutal !bg-brand-pink !text-white flex items-center gap-1"><Crown weight="bold" size={12} />VIP</span>;
  if (segment === "recorrente") return <span className="badge-brutal !bg-brand-mint">Recorrente</span>;
  return <span className="badge-brutal !bg-brand-yellow">Novo</span>;
}

function TierProgress({ h }) {
  if (h.segment === "vip") {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 border-2 border-brand-ink bg-brand-pink" />
        <span className="text-[10px] font-bold uppercase tracking-wide text-brand-ink">Nível máximo</span>
      </div>
    );
  }
  const pct = Math.round((h.progress_pct || 0) * 100);
  let label = "";
  let color = "bg-brand-mint";
  if (h.segment === "novo") {
    label = h.to_next_orders === 1
      ? "Falta 1 pedido para Recorrente"
      : `Faltam ${h.to_next_orders} pedidos para Recorrente`;
    color = "bg-brand-yellow";
  } else if (h.segment === "recorrente") {
    label = h.to_next_amount > 0
      ? `Faltam ${brl(h.to_next_amount)} para VIP`
      : "Prestes a virar VIP";
    color = "bg-brand-mint";
  }
  return (
    <div className="space-y-1">
      <div className="relative h-2 border-2 border-brand-ink bg-white overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[10px] font-bold text-brand-ink2 leading-tight">{label} · {pct}%</p>
    </div>
  );
}
